(function () {
  'use strict';

  const CART_ADD_PATTERN = /\/cart\/add(?:\.js)?(?:$|\?)/;
  let adjustedRefreshQueue = Promise.resolve();

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getRootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  function getAddUrl() {
    return window.routes?.cart_add_url || `${getRootUrl()}cart/add`;
  }

  function getCartUrl() {
    return window.routes?.cart_url || `${getRootUrl()}cart`;
  }

  async function parseResponse(response) {
    const text = await response.text();
    let data = {};

    try {
      data = text ? JSON.parse(text) : {};
    } catch (error) {
      data = {};
    }

    if (!response.ok || data.status) {
      const message = data.description || data.message || window.cartStrings?.error || 'Unable to update the cart.';
      const requestError = new Error(message);
      requestError.data = data;
      throw requestError;
    }

    return data;
  }

  function getSectionInnerHTML(html, selector = '.shopify-section') {
    const documentFragment = new DOMParser().parseFromString(html || '', 'text/html');
    const element = documentFragment.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  function getSectionDescriptors() {
    const descriptors = [
      {
        section: 'cart-drawer',
        target: '#CartDrawer',
        selector: '#CartDrawer',
      },
      {
        section: 'cart-icon-bubble',
        target: '#cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];

    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartItems?.dataset.id) {
      descriptors.push({
        section: cartItems.dataset.id,
        target: '#main-cart-items .js-contents',
        selector: '.js-contents',
      });
    }

    if (cartFooter?.dataset.id) {
      descriptors.push({
        section: cartFooter.dataset.id,
        target: '#main-cart-footer .js-contents',
        selector: '.js-contents',
      });
    }

    return descriptors.filter(
      (descriptor, index, items) =>
        items.findIndex((item) => item.section === descriptor.section) === index
    );
  }

  function getSectionNames() {
    return getSectionDescriptors().map((descriptor) => descriptor.section);
  }

  function rebindDrawer(drawer) {
    const overlay = drawer?.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
  }

  function updateEmptyStates(cartData, explicitIsEmpty) {
    let isEmpty = null;

    if (typeof explicitIsEmpty === 'boolean') {
      isEmpty = explicitIsEmpty;
    } else if (cartData && typeof cartData.item_count === 'number') {
      isEmpty = cartData.item_count === 0;
    }

    if (isEmpty === null) return;

    const drawer = document.querySelector('cart-drawer');
    drawer?.classList.toggle('is-empty', isEmpty);
    drawer?.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);

    document.getElementById('main-cart-items')?.classList.toggle('is-empty', isEmpty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
  }

  function renderSections(state, options = {}) {
    const drawer = getCartDrawer();
    const shouldOpen = options.open === true;
    const sections = state?.sections || state;

    if (!sections) {
      if (shouldOpen) window.location.href = getCartUrl();
      return;
    }

    getSectionDescriptors().forEach((descriptor) => {
      const html = sections[descriptor.section];
      const target = document.querySelector(descriptor.target);

      if (!html || !target) return;

      const replacement = getSectionInnerHTML(html, descriptor.selector);
      if (replacement !== '') target.innerHTML = replacement;
    });

    updateEmptyStates(options.cartData || state, options.isEmpty);
    rebindDrawer(drawer);

    document.dispatchEvent(
      new CustomEvent('ls:cart-rendered', {
        detail: {
          cartData: options.cartData || state,
          opened: shouldOpen,
        },
      })
    );

    if (shouldOpen) {
      if (drawer) drawer.open(options.trigger || null);
      else window.location.href = getCartUrl();
    }
  }

  function setButtonLoading(button, loading) {
    if (!button) return;

    if (loading) {
      button.dataset.lsWasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      return;
    }

    button.classList.remove('loading');

    if (button.dataset.lsWasDisabled !== 'true') {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    }

    delete button.dataset.lsWasDisabled;
  }

  function showFormError(form, message) {
    if (!form) return;

    let errorElement = form.querySelector('[data-ls-cart-error]') || form.querySelector('.product-form__error-message');

    if (!errorElement) {
      errorElement = document.createElement('p');
      errorElement.setAttribute('data-ls-cart-error', '');
      errorElement.setAttribute('role', 'alert');
      errorElement.className = 'product-form__error-message';
      form.appendChild(errorElement);
    }

    errorElement.textContent = message;
    errorElement.hidden = false;
    errorElement.style.display = '';

    const wrapper = errorElement.closest('.product-form__error-message-wrapper');
    if (wrapper) wrapper.hidden = false;
  }

  function clearFormError(form) {
    const errorElement = form?.querySelector('[data-ls-cart-error]');
    if (errorElement) errorElement.hidden = true;
  }

  function appendSectionRequest(formData) {
    formData.set('sections', getSectionNames().join(','));
    formData.set('sections_url', window.location.pathname);
    return formData;
  }

  async function finaliseCartMutation(options = {}) {
    const controller = window.LSCartState;

    if (!controller) return { cart: options.fallbackState || null, changed: false };

    controller.invalidate();

    try {
      const result = await controller.sync({
        force: true,
        source: options.source || 'ls-ajax-cart',
        notify: false,
        emitAdjusted: false,
      });

      if (result.changed) {
        await refresh({
          open: false,
          cartData: result.cart,
          notify: false,
        });
      }

      controller.notify(result.cart, `ls-cart-state-${options.source || 'ajax-final'}`, {
        publish: true,
        skipSectionRefresh: true,
        adjusted: result.changed,
      });

      return result;
    } catch (error) {
      console.error('[Love Smiths] Post-add cart synchronisation error:', error);

      try {
        controller.invalidate();
        const cart = await controller.getCart({ force: true });
        controller.notify(cart, `ls-cart-state-${options.source || 'ajax-fallback'}-fallback`, {
          publish: true,
          skipSectionRefresh: true,
        });
        return { cart, changed: false, error };
      } catch (cartError) {
        console.error('[Love Smiths] Cart recovery error:', cartError);
        return { cart: options.fallbackState || null, changed: false, error };
      }
    }
  }

  async function addForm(form, options = {}) {
    const submitter = options.submitter || null;
    const formData = appendSectionRequest(new FormData(form));
    const variantId = formData.get('id');

    if (!variantId) {
      throw new Error(window.variantStrings?.unavailable || window.cartStrings?.error || 'Please select a product option.');
    }

    const response = await fetch(getAddUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
      credentials: 'same-origin',
    });

    const state = await parseResponse(response);
    renderSections(state, {
      open: options.open !== false,
      trigger: submitter,
      isEmpty: false,
    });

    const result = await finaliseCartMutation({
      source: options.source || 'ls-ajax-cart',
      fallbackState: state,
    });

    return result.cart || state;
  }

  async function addItems(items, options = {}) {
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error(window.cartStrings?.error || 'No products were supplied.');
    }

    const response = await fetch(`${getRootUrl()}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items,
        sections: getSectionNames(),
        sections_url: window.location.pathname,
      }),
    });

    const state = await parseResponse(response);
    renderSections(state, {
      open: options.open !== false,
      trigger: options.trigger || null,
      isEmpty: false,
    });

    const result = await finaliseCartMutation({
      source: options.source || 'ls-ajax-cart-multi',
      fallbackState: state,
    });

    return result.cart || state;
  }

  async function refresh(options = {}) {
    const descriptors = getSectionDescriptors();
    const sectionNames = descriptors.map((descriptor) => descriptor.section);
    const params = new URLSearchParams({ sections: sectionNames.join(',') });

    const response = await fetch(`${getCartUrl()}?${params.toString()}`, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    });

    const sections = await parseResponse(response);
    let cartData = options.cartData || null;

    if (!cartData && window.LSCartState) {
      cartData = await window.LSCartState.getCart({ force: false });
    }

    if (!cartData) {
      const cartResponse = await fetch(`${getRootUrl()}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      cartData = await parseResponse(cartResponse);
    }

    renderSections(
      {
        ...cartData,
        sections,
      },
      {
        open: options.open === true,
        trigger: options.trigger || null,
        cartData,
      }
    );

    if (options.notify === true && window.LSCartState) {
      window.LSCartState.notify(cartData, options.source || 'ls-cart-state-refresh', {
        publish: options.publish === true,
        skipSectionRefresh: true,
      });
    }

    return cartData;
  }

  function isEligibleCustomForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.closest('product-form')) return false;
    if (form.id === 'CartDrawer-Form' || form.id === 'cart') return false;

    const action = form.getAttribute('action') || form.action || '';
    return CART_ADD_PATTERN.test(action);
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;

    if (!isEligibleCustomForm(form) || event.defaultPrevented) return;

    event.preventDefault();
    clearFormError(form);

    const submitter = event.submitter || form.querySelector('[type="submit"]');
    setButtonLoading(submitter, true);

    try {
      await addForm(form, {
        submitter,
        source: 'ls-custom-product-form',
      });
    } catch (error) {
      console.error('[Love Smiths] Add to cart error:', error);
      showFormError(form, error.message || window.cartStrings?.error || 'Unable to update the cart.');
    } finally {
      setButtonLoading(submitter, false);
    }
  });

  document.addEventListener('ls:cart-adjusted', (event) => {
    const cartData = event.detail?.cartData;
    const source = event.detail?.source || 'ls-cart-state-adjusted';

    if (!window.LSCartState?.isFullCart(cartData)) return;

    adjustedRefreshQueue = adjustedRefreshQueue
      .then(async () => {
        await refresh({ open: false, cartData, notify: false });
        window.LSCartState.notify(cartData, 'ls-cart-state-adjusted-final', {
          publish: true,
          skipSectionRefresh: true,
          adjusted: true,
        });
      })
      .catch((error) => {
        console.error(`[Love Smiths] Cart section refresh error after ${source}:`, error);
        window.LSCartState.notify(cartData, 'ls-cart-state-adjusted-recovery', {
          publish: true,
          skipSectionRefresh: true,
          adjusted: true,
        });
      });
  });

  window.LSAjaxCart = {
    addForm,
    addItems,
    refresh,
    renderSections,
  };
})();
